"use client";

import React from "react";
import { ArrowRight, Music, Star, Users, Clock, Award } from "lucide-react";

export const generateMetadata = () => ({
  title: "Khóa Học Guitar Online - Học Guitar Cùng CuongGuitar",
  description:
    "Khóa học guitar thân thiện, dễ hiểu và phù hợp cho mọi trình độ.",
  alternates: {
    canonical: "https://guitarsaigon.com/dang-ky-hoc-guitar",
  },
});

const StructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: "Khóa Học Guitar Chuyên Nghiệp",
    provider: {
      "@type": "Organization",
      name: "Guitar Academy",
      sameAs: "https://guitarsaigon.com",
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

interface CourseWrapperProps {
  headline?: string;
  description?: string;
  imageUrl?: string;
  ctaText?: string;
  ctaLink?: string;
  children?: React.ReactNode;
  imagePosition?: "left" | "right";
}

const stats = [
  { icon: Users, value: "10.000+", label: "Học viên" },
  { icon: Clock, value: "12 tuần", label: "Lộ trình" },
  { icon: Award, value: "98%",     label: "Hài lòng" },
];

const benefits = [
  "Thực hành nhiều – không học vẹt",
  "Tiến bộ tự nhiên theo tốc độ của bạn",
  "Có người hướng dẫn từng bước",
  "Chơi được bài bạn yêu thích",
];

const CourseWrapper: React.FC<CourseWrapperProps> = ({
  headline     = "Thành Thạo Guitar Trong 12 Tuần",
  description  = "Học Guitar",
  imageUrl,
  ctaText      = "Đăng Ký Ngay",
  ctaLink      = "/dang-ky-hoc-guitar",
  children,
  imagePosition = "left",
}) => {
  return (
    <>
      <StructuredData />

      <article
        /* ── Mobile: py-10 (40px); md+: py-16; lg+: py-20 ── */
        className="w-full py-10 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: "var(--section-bg)", color: "var(--section-foreground)" }}
        itemScope
        itemType="https://schema.org/Course"
      >
        <div className="max-w-7xl mx-auto">

          {/* ══════════════════════════════════════
              HEADER
          ══════════════════════════════════════ */}
          {/* Mobile: mb-8; md+: mb-12; lg+: mb-16 */}
          <header className="text-center mb-8 md:mb-12 lg:mb-16">

            {/* Badge — smaller on mobile */}
            <div
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 border"
              style={{
                backgroundColor: "rgba(200,169,110,0.1)",
                borderColor: "rgba(200,169,110,0.35)",
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
              }}
            >
              <Music className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Khóa Học Được Yêu Thích
            </div>

            {/*
              Headline sizes (mobile-first):
              - 375px  → text-2xl  (24px) — fits in 1–2 lines, readable
              - 640px+ → text-4xl  (36px)
              - 768px+ → text-5xl  (48px)
              - 1024px+→ text-6xl  (60px)
            */}
            <h1
              className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 sm:mb-5 leading-[1.15] tracking-tight px-1"
              style={{
                fontFamily: "var(--font-montserrat)",
                color: "var(--section-foreground)",
                wordBreak: "keep-all",       /* prevent mid-word break on vi text */
                overflowWrap: "break-word",
              }}
              itemProp="name"
            >
              {headline}
            </h1>

            {/* Description — smaller on mobile, max-width relaxed */}
            <p
              className="text-sm sm:text-lg max-w-sm sm:max-w-xl md:max-w-2xl mx-auto leading-relaxed px-1"
              style={{ color: "var(--section-muted)", fontFamily: "var(--font-roboto)" }}
              itemProp="description"
            >
              {description}
            </p>

            {/* Gold divider */}
            <div className="mt-5 sm:mt-8 flex items-center justify-center gap-3">
              <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.3)" }} />
              <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
              <div className="h-px w-10 sm:w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.3)" }} />
            </div>
          </header>

          {/* ══════════════════════════════════════
              STATS ROW
              3 columns always — but tighter on mobile
          ══════════════════════════════════════ */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-12 max-w-xs sm:max-w-sm md:max-w-2xl mx-auto">
            {stats.map(({ icon: Icon, value, label }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-0.5 sm:gap-1 py-3.5 sm:py-5 px-1.5 sm:px-3 rounded-xl sm:rounded-2xl border"
                style={{
                  backgroundColor: "var(--section-card)",
                  borderColor: "var(--section-border)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-0.5" style={{ color: "var(--brand-primary)" }} />
                <span
                  className="text-base sm:text-xl md:text-2xl font-black leading-none"
                  style={{ fontFamily: "var(--font-montserrat)", color: "var(--section-foreground)" }}
                >
                  {value}
                </span>
                <span
                  className="text-[9px] sm:text-xs font-medium text-center leading-tight mt-0.5"
                  style={{ color: "var(--section-muted)" }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* ══════════════════════════════════════
              HERO CARD
          ══════════════════════════════════════ */}
          <section
            className="rounded-2xl sm:rounded-3xl overflow-hidden border"
            style={{
              backgroundColor: "var(--section-card)",
              borderColor: "var(--section-border)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(200,169,110,0.08)",
            }}
          >
            {/*
              Layout:
              - Mobile: flex-col — image stacked on top, content below
              - lg+: side-by-side grid
            */}
            <div
              className={`flex flex-col lg:grid lg:grid-cols-2 ${
                imagePosition === "right" ? "lg:[direction:rtl]" : ""
              }`}
            >

              {/* ── IMAGE PANEL ── */}
              {/*
                Mobile:  h-52 (208px) — enough to show image without eating screen
                sm:      h-64 (256px)
                lg+:     auto height, min-h-[460px] — fills grid row
              */}
              <div className="relative h-52 sm:h-64 lg:h-auto lg:min-h-[460px] overflow-hidden lg:[direction:ltr]">
                <img
                  src={imageUrl}
                  alt="Học viên đang học guitar"
                  className="w-full h-full object-cover"
                  loading="eager"
                  style={{ filter: "brightness(0.88) contrast(1.05)" }}
                />

                {/* Gradient — stronger at bottom so text is always legible */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 45%, transparent 70%)",
                  }}
                />

                {/* Gold top bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ background: "var(--brand-primary)" }}
                />

                {/* Floating badge — slightly smaller on mobile */}
                <div
                  className="absolute top-3 sm:top-5 left-3 sm:left-5 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border backdrop-blur-sm"
                  style={{
                    backgroundColor: "rgba(13,13,13,0.72)",
                    borderColor: "rgba(200,169,110,0.4)",
                    color: "var(--brand-primary)",
                    fontFamily: "var(--font-montserrat)",
                  }}
                >
                  <Music className="w-3 h-3 sm:w-4 sm:h-4" />
                  Khóa Học Guitar
                </div>

                {/* Bottom label */}
                <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5">
                  <div className="flex items-center gap-0.5 sm:gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current"
                        style={{ color: "var(--brand-primary)" }}
                      />
                    ))}
                  </div>
                  <p
                    className="text-white text-xs sm:text-sm font-medium leading-snug"
                    style={{ fontFamily: "var(--font-roboto)" }}
                  >
                    "Học xong là chơi được – không phải hứa suông"
                  </p>
                </div>
              </div>

              {/* ── CONTENT PANEL ── */}
              {/*
                Mobile:  p-5   — comfortable but compact
                sm:      p-7
                lg+:     p-10 / p-12
              */}
              <div className="p-5 sm:p-7 lg:p-10 xl:p-12 flex flex-col justify-center lg:[direction:ltr]">

                <p
                  className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2.5 sm:mb-4"
                  style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                >
                  Tại sao chọn chúng tôi?
                </p>

                <p
                  className="text-sm leading-relaxed mb-5 sm:mb-8"
                  style={{ color: "var(--section-muted)", fontFamily: "var(--font-roboto)" }}
                >
                  Từ hợp âm đầu tiên đến kỹ thuật nâng cao, chúng tôi đồng hành cùng bạn trên hành trình chinh phục guitar — bằng phương pháp thực chiến, không lý thuyết khô khan.
                </p>

                {/* Benefits list */}
                <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-10">
                  {benefits.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 sm:gap-3">
                      <div
                        className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-[10px] sm:text-xs font-black mt-0.5"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.15)",
                          color: "var(--brand-primary)",
                          border: "1.5px solid rgba(200,169,110,0.35)",
                        }}
                      >
                        ✓
                      </div>
                      <span
                        className="text-sm leading-relaxed"
                        style={{
                          color: "var(--section-foreground)",
                          fontFamily: "var(--font-roboto)",
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {/*
                  CTA button:
                  - Mobile: w-full, easier to tap (min 48px height)
                  - sm+:    inline (auto width)
                */}
                <a
                  href={ctaLink}
                  className="group inline-flex items-center justify-center gap-2 sm:gap-3 font-bold px-6 sm:px-8 rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98] w-full sm:w-auto"
                  style={{
                    backgroundColor: "var(--brand-primary)",
                    color: "var(--brand-primary-foreground)",
                    fontFamily: "var(--font-montserrat)",
                    fontSize: "0.875rem",
                    boxShadow: "0 4px 20px rgba(200,169,110,0.3)",
                    height: "52px",          /* consistent tap target */
                  }}
                >
                  <span>{ctaText}</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:translate-x-1" />
                </a>

                {/* Trust line */}
                <div
                  className="mt-5 sm:mt-8 pt-4 sm:pt-7 flex items-center gap-2.5 sm:gap-3 border-t"
                  style={{ borderColor: "var(--section-border)" }}
                >
                  <div className="flex -space-x-1.5 sm:-space-x-2">
                    {["CA", "BT", "ML"].map((init) => (
                      <div
                        key={init}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.15)",
                          borderColor: "var(--section-bg)",
                          color: "var(--brand-primary)",
                          fontFamily: "var(--font-montserrat)",
                        }}
                      >
                        {init}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs sm:text-sm" style={{ color: "var(--section-muted)" }}>
                    <span
                      className="font-semibold"
                      style={{ color: "var(--section-foreground)" }}
                    >
                      10.000+
                    </span>{" "}
                    học viên đã tin tưởng
                  </p>
                </div>
              </div>

            </div>
          </section>

          {/* ── OPTIONAL CHILDREN ── */}
          {children && (
            <section
              className="mt-5 sm:mt-10 rounded-2xl border p-5 sm:p-8"
              style={{
                backgroundColor: "var(--section-card)",
                borderColor: "var(--section-border)",
              }}
            >
              {children}
            </section>
          )}

        </div>
      </article>
    </>
  );
};

const CourseGuitarWrapper = () => (
  <CourseWrapper
    headline="Khóa Học Đàn Guitar"
    description="Học guitar không chỉ là ghi nhớ, mà là cảm nhận và sống cùng từng giai điệu."
    imageUrl="https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=800&h=600&fit=crop"
    ctaText="Xem Cách Đăng Ký"
    ctaLink="/dang-ky-hoc-guitar"
    imagePosition="left"
  />
);

export default CourseGuitarWrapper;